import type { IPendingRequest } from "src/redux/slices/pendingRequestsSlice";

export type PendingRequestGroup = {
  entryId: string;
  logbookName?: string;
  runs: PendingRequestRun[];
  sortKey: number;
};

export type PendingRequestRun = {
  kind: "autosaves" | "checklists" | "saves";
  requests: IPendingRequest[];
};

export type PendingRequestDisplayItem =
  | { kind: "group"; group: PendingRequestGroup }
  | { kind: "row"; req: IPendingRequest; sortKey: number };

const readEntryKey = (
  req: IPendingRequest,
): { entryId: string; kind: PendingRequestRun["kind"] } | null => {
  const [type, entryId] = req.req.split("__");
  if (!entryId) return null;
  if (req.isAutosave) return { entryId, kind: "autosaves" };
  if (type === "checklist-action") return { entryId, kind: "checklists" };
  if (type === "save-entry") return { entryId, kind: "saves" };
  return null;
};

export const groupPendingRequests = (
  requests: IPendingRequest[],
): PendingRequestDisplayItem[] => {
  const groupedRequests = new Map<
    string,
    Array<{ req: IPendingRequest; kind: PendingRequestRun["kind"] }>
  >();
  const groupedReqIds = new Set<string>();

  requests.forEach(req => {
    const key = readEntryKey(req);
    if (!key) return;

    groupedReqIds.add(req.req);
    const entryRequests = groupedRequests.get(key.entryId) ?? [];
    entryRequests.push({ req, kind: key.kind });
    groupedRequests.set(key.entryId, entryRequests);
  });

  const blocks: PendingRequestDisplayItem[] = [];

  groupedRequests.forEach((entryRequests, entryId) => {
    const orderedRequests = entryRequests.sort((a, b) => {
      const timeOrder = a.req.created - b.req.created;
      if (timeOrder !== 0) return timeOrder;
      return (a.req.queueOrder ?? 0) - (b.req.queueOrder ?? 0);
    });
    const runs = orderedRequests.reduce<PendingRequestRun[]>(
      (result, { req, kind }) => {
        const lastRun = result[result.length - 1];
        if (lastRun?.kind === kind) {
          lastRun.requests.push(req);
        } else {
          result.push({ kind, requests: [req] });
        }
        return result;
      },
      [],
    );
    const sortKey = orderedRequests[orderedRequests.length - 1].req.created;
    const logbookName = orderedRequests.find(({ req }) => req.lbname)?.req
      .lbname;

    blocks.push({
      kind: "group",
      group: {
        entryId,
        logbookName,
        runs,
        sortKey,
      },
    });
  });

  requests.forEach(req => {
    if (groupedReqIds.has(req.req)) return;
    blocks.push({ kind: "row", req, sortKey: req.created });
  });

  return blocks.sort((a, b) => {
    const aKey = a.kind === "group" ? a.group.sortKey : a.sortKey;
    const bKey = b.kind === "group" ? b.group.sortKey : b.sortKey;
    return bKey - aKey;
  });
};
